/* eslint-disable max-lines -- one route page composing the rail, filter toolbar, and list query; like the sibling list routes, splitting it would scatter one page's wiring across files. */
import {
  DimeCard,
  DimeDataTable,
  type DimeDateRange,
  localDateToYmd,
  ymdToLocalNoon,
} from '@nudool/design-system';
import { MAX_SEARCH_TERM_LENGTH } from '@nudool/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CalendarDays } from 'lucide-react';
import { useMemo } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import type { CalEventWhere } from '../../generated/graphql/graphql.js';
import { useBusinessDayNow } from '../../hooks/use-business-day-now.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { RouteGuard } from '../../lib/route-guard.js';
import { businessDayBoundaries } from '../../modules/events/business-day-boundaries.js';
import { EventsCalendar } from '../../modules/events/calendar/events-calendar.js';
import {
  countEventListFilters,
  eventListFiltersFromSearch,
  type EventListFilters,
} from '../../modules/events/event-list-filters.js';
import { EVENT_SORT_MAP } from '../../modules/events/event-sort-map.js';
import { eventWorkspaceQueueForViewer } from '../../modules/events/event-workspace-queue-for-viewer.js';
import { makeEventColumns } from '../../modules/events/events-columns.js';
import { EventsFilterToolbar } from '../../modules/events/events-filter-toolbar.js';
import { EventsHeaderBar } from '../../modules/events/events-header-bar.js';
import {
  parseEventsListSearch,
  parseStatusIdsParam,
  type EventsListSearch,
} from '../../modules/events/events-list-search.js';
import { buildEventsListWhere } from '../../modules/events/events-list-where.js';
import {
  EVENT_WORKSPACE_QUEUES,
  parseEventWorkspaceQueueParam,
  type EventWorkspaceQueue,
  statusIdsAdmittedByQueue,
} from '../../modules/events/events-workspace-queues.js';
import { EventsWorkspaceRail } from '../../modules/events/events-workspace-rail.js';
import { isDefaultEventBrandSelection } from '../../modules/events/is-default-event-brand-selection.js';
import {
  eventsConnectionFromQuery,
  GET_EVENTS,
  type EventListItem,
  type GetEventsQueryResult,
  type GetEventsQueryVariables,
} from '../../modules/events/operations.js';
import { useDefaultEventBrandSelection } from '../../modules/events/use-default-event-brand-selection.js';
import { useEventMetrics } from '../../modules/events/use-event-metrics.js';
import { useEventsFilterHandlers } from '../../modules/events/use-events-filter-handlers.js';
import { useEventsFilterOptions } from '../../modules/events/use-events-filter-options.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';

const EVENT_DEFAULT_SORT = [{ id: 'dateStart', desc: true }];

export const Route = createFileRoute('/events/')({
  validateSearch: (search: Record<string, unknown>): EventsListSearch =>
    parseEventsListSearch(search),
  component: EventsIndexPage,
});

// eslint-disable-next-line react-refresh/only-export-components -- the route file exports Route and registers its page component by reference.
function EventsList() {
  const routeSearch = Route.useSearch();
  const navigate = useNavigate({ from: '/events/' });
  const { permissions } = usePermissions();
  const canApprove = permissions.includes('event:approve');
  const queue = parseEventWorkspaceQueueParam(routeSearch.queue, canApprove);

  const statusIds = useMemo(
    () => statusIdsAdmittedByQueue(queue, parseStatusIdsParam(routeSearch.statusIds)),
    [routeSearch.statusIds, queue],
  );
  const filters = useMemo(
    () => ({
      ...eventListFiltersFromSearch(routeSearch),
      statusIds: statusIds.length > 0 ? statusIds : undefined,
    }),
    [routeSearch, statusIds],
  );
  const dateRange: DimeDateRange | undefined = useMemo(() => {
    if (!routeSearch.dateFrom && !routeSearch.dateTo) return undefined;
    return {
      from: routeSearch.dateFrom ? ymdToLocalNoon(routeSearch.dateFrom) : undefined,
      to: routeSearch.dateTo ? ymdToLocalNoon(routeSearch.dateTo) : undefined,
    };
  }, [routeSearch.dateFrom, routeSearch.dateTo]);
  const selectedBrandIds = useMemo(() => filters.brandIds ?? [], [filters.brandIds]);
  const {
    brands,
    brandsError,
    brandsTruncated,
    myBrands,
    stores,
    storesError,
    storesTruncated,
    onStoreSearchChange,
    assignees,
    assigneesError,
    assigneesTruncated,
    templates,
    templatesError,
    templatesTruncated,
  } = useEventsFilterOptions({ brandIds: selectedBrandIds, canApprove });
  const [businessDayNow] = useBusinessDayNow();
  const dayBoundaries = useMemo(() => businessDayBoundaries(businessDayNow), [businessDayNow]);

  const where = useMemo(
    () =>
      buildEventsListWhere({
        brandIds: filters.brandIds,
        storeIds: filters.storeIds,
        dateFrom: dateRange?.from && localDateToYmd(dateRange.from),
        dateTo: dateRange?.to && localDateToYmd(dateRange.to),
        statusIds,
      }),
    [filters.brandIds, filters.storeIds, dateRange, statusIds],
  );

  const listVariables = {
    typeValues:
      filters.typeValues && filters.typeValues.length > 0 ? filters.typeValues : undefined,
    assignedToMe: routeSearch.assignedToMe || undefined,
    assigneeId: canApprove ? routeSearch.assigneeId : undefined,
    queue,
    today: dayBoundaries.today,
    tomorrow: dayBoundaries.tomorrow,
    weekEnd: dayBoundaries.weekEnd,
    withOpenTasks: queue === 'underReview',
  };
  const {
    rows: events,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<GetEventsQueryResult, GetEventsQueryVariables, EventListItem, CalEventWhere>({
    query: GET_EVENTS,
    extractConnection: eventsConnectionFromQuery,
    defaultSort: EVENT_DEFAULT_SORT,
    sortMap: EVENT_SORT_MAP,
    sortTiebreakerField: null,
    where,
    variables: listVariables,
  });

  function navigateSearch(patch: (previous: EventsListSearch) => EventsListSearch) {
    tableProps.onFirstPage();
    void navigate({
      to: '/events',
      search: (previous) => patch(previous),
    });
  }
  const filterHandlers = useEventsFilterHandlers(navigateSearch);

  useDefaultEventBrandSelection({
    brandIdsParam: routeSearch.brandIds,
    myBrands,
    onApplied: tableProps.onFirstPage,
  });

  const applyEventFilters = (next: EventListFilters, searchPatch?: Partial<EventsListSearch>) =>
    filterHandlers.onEventFiltersChange(next, { selectedBrandIds, queue }, searchPatch);

  const metrics = useEventMetrics(selectedBrandIds, dayBoundaries, canApprove);

  const rowOffset = tableProps.pageIndex * tableProps.pageSize;
  const columns = useMemo(() => makeEventColumns({ rowOffset }), [rowOffset]);

  // parseEventWorkspaceQueueParam guarantees queue is a valid member; find always returns a value.
  const queueMetadata = eventWorkspaceQueueForViewer(
    EVENT_WORKSPACE_QUEUES.find((item) => item.id === queue)!,
    canApprove,
  );
  const hasActiveFilters =
    countEventListFilters(
      isDefaultEventBrandSelection(selectedBrandIds, myBrands)
        ? { ...filters, brandIds: undefined }
        : filters,
    ) > 0 ||
    Boolean(dateRange) ||
    Boolean(routeSearch.assignedToMe) ||
    Boolean(routeSearch.assigneeId);
  const showEmptyState =
    !loading && !error && events.length === 0 && !search && !hasActiveFilters && queue === 'all';
  const narrowed = hasActiveFilters || Boolean(search);

  function handleQueueChange(nextQueue: EventWorkspaceQueue) {
    navigateSearch((previous) => {
      const next = { ...previous, queue: nextQueue };
      const admitted = statusIdsAdmittedByQueue(nextQueue, statusIds);
      return { ...next, statusIds: admitted.length > 0 ? admitted.join(',') : undefined };
    });
  }

  return (
    <DimeCard borderless className="gap-0 py-0">
      <EventsHeaderBar
        view="list"
        onViewChange={(view) => navigateSearch((previous) => ({ ...previous, view }))}
      />

      {error && events.length === 0 ? (
        <ListErrorState
          title="Could not load events"
          message={error.message}
          onRetry={() => {
            refetch().catch(() => undefined);
          }}
        />
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <aside className="w-full shrink-0 lg:w-56">
            <EventsWorkspaceRail
              queue={queue}
              metrics={metrics}
              canApprove={canApprove}
              onQueueChange={handleQueueChange}
            />
          </aside>
          <div className="min-w-0 flex-1 space-y-3">
            {showEmptyState ? (
              <ListEmptyState
                icon={<CalendarDays />}
                title={queueMetadata.emptyTitle}
                description={queueMetadata.emptyDescription}
              />
            ) : (
              <DimeDataTable
                columns={columns}
                data={events}
                maxBodyHeight="viewport"
                searchColumn="name"
                searchPlaceholder="Search by event name…"
                searchMaxLength={MAX_SEARCH_TERM_LENGTH}
                filters={
                  <EventsFilterToolbar
                    filters={filters}
                    onFiltersChange={applyEventFilters}
                    templates={templates}
                    stores={stores}
                    onStoreSearchChange={onStoreSearchChange}
                    brands={brands}
                    searchErrors={{
                      brandIds: brandsError,
                      storeIds: storesError,
                      typeValues: templatesError,
                    }}
                    truncated={{
                      brandIds: brandsTruncated,
                      storeIds: storesTruncated,
                      typeValues: templatesTruncated,
                    }}
                    dateRange={dateRange}
                    onDateRangeChange={filterHandlers.onDateRangeChange}
                    assignedToMe={routeSearch.assignedToMe ?? false}
                    onAssignedToMeChange={filterHandlers.onAssignedToMeChange}
                    assignees={assignees}
                    assigneesError={assigneesError}
                    assigneesTruncated={assigneesTruncated}
                    assigneeId={routeSearch.assigneeId}
                    onAssigneeIdChange={filterHandlers.onAssigneeIdChange}
                  />
                }
                showViewOptions
                emptyMessage={
                  narrowed
                    ? 'No events match your filters.'
                    : `${queueMetadata.emptyTitle}. ${queueMetadata.emptyDescription}`
                }
                onRowClick={(event) => {
                  if (!event.id) return;
                  void navigate({
                    to: '/events/$eventId',
                    params: { eventId: event.id },
                    search: { edit: false },
                  });
                }}
                {...tableProps}
              />
            )}
          </div>
        </div>
      )}
    </DimeCard>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- the route file exports Route and registers its page component by reference.
function EventsIndexPage() {
  const routeSearch = Route.useSearch();
  return (
    <RouteGuard route="/events">
      {routeSearch.view === 'list' ? <EventsList /> : <EventsCalendar routeSearch={routeSearch} />}
    </RouteGuard>
  );
}
